/**
 * Permission-management data hooks (full-admin only).
 *
 * TanStack Query layer over the `adminApi` permission endpoints, mirroring
 * `useOrg.ts`: every write is an ACL change, so it is **server-authoritative**
 * (the clinical-safety carve-out in `frontend/CLAUDE.md`) — NO optimistic
 * updates. Each mutation calls the API, then invalidates the affected grant
 * list so the row appears/disappears only once the server confirms it.
 *
 * The grantable-target vocabulary comes from `/grants:targets`; the
 * screen never hardcodes a relation or a resource type.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { AUTHORIZATION_QUERY, QUERY_STALE_TIMES } from '@/lib/query-config';
import {
  adminApi,
  type ListPermissionsResponse,
  type PermissionGrantBody,
  type PermissionTargetsResponse,
} from '@/api/endpoints/admin';

// --- Query keys ---------------------------------------------------------------

export const permissionKeys = {
  all: ['permissions'] as const,
  targets: () => [...permissionKeys.all, 'targets'] as const,
  grants: (resourceType: string, resourceId: string) =>
    [...permissionKeys.all, 'grants', resourceType, resourceId] as const,
};

// --- Queries ------------------------------------------------------------------

/**
 * The grantable-target vocabulary (endpoints, admin tiers, and their relations).
 * Cached on the org vocabulary cadence — it changes only when the platform's
 * authz model does.
 */
export function usePermissionTargets() {
  return useAuthenticatedQuery<PermissionTargetsResponse>({
    queryKey: permissionKeys.targets(),
    queryFn: () => adminApi.getPermissionTargets(),
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
}

/**
 * Current grants for one resource. `enabled` only once a target is picked; the
 * 'none' key keeps the hook order stable while nothing is selected.
 *
 * This hook shipped with `refetchOnWindowFocus: false` and no `staleTime`, so
 * an access review answered "who can reach this?" from the global
 * `sessions_stale_ms` cache with nothing to invalidate it. AUTHORIZATION_QUERY
 * carries the cadence and the reason.
 */
export function usePermissionGrants(resourceType: string | null, resourceId: string | null) {
  const ready = !!resourceType && !!resourceId;
  return useAuthenticatedQuery<ListPermissionsResponse>({
    queryKey: ready
      ? permissionKeys.grants(resourceType, resourceId)
      : [...permissionKeys.all, 'grants', 'none'],
    queryFn: () => adminApi.listPermissions(resourceType as string, resourceId as string),
    enabled: ready,
    ...AUTHORIZATION_QUERY,
  });
}

// --- Mutations (server-authoritative) -----------------------------------------

export function useGrantPermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PermissionGrantBody) => adminApi.grantPermission(body),
    onSuccess: (_data, body) =>
      qc.invalidateQueries({
        queryKey: permissionKeys.grants(body.resource_type, body.resource_id),
      }),
  });
}

export function useRevokePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PermissionGrantBody) => adminApi.revokePermission(body),
    onSuccess: (_data, body) =>
      qc.invalidateQueries({
        queryKey: permissionKeys.grants(body.resource_type, body.resource_id),
      }),
  });
}
